import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import {
  makeStyles,
  TableContainer,
  Table,
  Typography,
  Grid,
  TableBody,
  Box,
  IconButton,
  Collapse,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";

import { api } from "../../../../../services/api";
import { COLORS } from "../../../../../theme/colors";
import { PowerAssetIcon } from "../../../PowerAssetIcon";
import { currencyFormatter, getLabel, nKilowattFormatter } from "../../../../../services/utils";
import { StatusBox } from "../../../../../components/boxes/StatusBox";
import { ActionButtonsGroup } from "../../../../../components/buttonsGroup/ActionButtonsGroup";
import {
  StyledTableRow,
  StyledTableCell,
  EnhancedTableHead,
} from "../../../../../components/base/Table";
import { useComputePowerAssetsTotal } from "../useComputePowerAssetsTotal";
import { statusCountObjectToString } from "./statusCountObjectToString";

const useStyles = makeStyles(() => ({
  bold: {
    fontWeight: "bold",
  },
  locationCell: {
    width: 300,
  },
  activityCell: {
    width: 340,
  },
  tableContainer: {
    borderRadius: 8,
    maxHeight: 556,
    border: `1px solid ${COLORS.darks[0][10]}`,
    boxShadow: "none",
  },
  capitalize: {
    textTransform: "capitalize",
  },
}));

export const getActivity = (activities) => {
  if (activities) {
    const activity = activities[activities.length - 1];
    const date = new Date(activity.date);
    const year = date.getUTCFullYear();
    const month = date.toLocaleString("en-us", { month: "short" });
    const day = date.getUTCDate();
    return `${month} ${day}, ${year} - ${activity.text}`;
  }
};

const headCells = [
  {
    id: "location",
    label: "Location",
  },
  {
    id: "type",
    label: "Sys. Name",
  },
  {
    id: "production",
    label: "Production (kWh)",
  },
  {
    id: "acc_revenue",
    label: "Acc. Revenues",
  },
  {
    id: "status",
    label: "Status",
  },
  {
    label: "Recent Activity",
  },
];

const initialData = {
  count: 0,
  previous: null,
  next: null,
  results: [],
};

export const ByTypeTable = ({ name, type, showActionButtons, setId, setIsList }) => {
  const classes = useStyles();
  const [data, setData] = useState(initialData);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("location");
  const [open, setOpen] = useState(true);
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);
  const [totalProduction, totalAccumulatedRevenue, statusCountObject] = useComputePowerAssetsTotal(
    data.results
  );

  const getData = (filter) => {
    const url = `/praas/v1/power-assets/list-by-user/${filter}`;
    api.get(url).then((resp) => {
      setData(resp.data);
    });
  };

  useEffect(() => {
    getData(`?type=${type.toUpperCase()}`);
  }, [type]);

  const handleClick = (id) => {
    setId(id);
    setIsList(false);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    getData(`?type=${type.toUpperCase()}&ordering=${isAsc ? "-" : ""}${property}`);
  };

  return (
    <div style={{ marginBottom: 30 }} id={type}>
      <Grid container alignItems="center" style={{ marginBottom: 20 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PowerAssetIcon type={type} />
          <Typography variant="h4">
            {name}({data.results.length})
          </Typography>
        </Box>
        {showActionButtons && <ActionButtonsGroup hideIntegrateBtn />}
      </Grid>
      <TableContainer className={classes.tableContainer}>
        <Collapse in={open} timeout="auto" collapsedSize={119.825}>
          <Table aria-label="simple table">
            <EnhancedTableHead
              headCells={headCells}
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              <StyledTableRow>
                <StyledTableCell colSpan={2}>
                  <IconButton size="small" onClick={() => setOpen(!open)}>
                    {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRight />}
                  </IconButton>
                  <Typography variant="h5" component="span" className={classes.bold}>
                    {data.results.length} Buildings
                  </Typography>
                </StyledTableCell>
                <StyledTableCell align="left">
                  <Typography variant="body1">{nKilowattFormatter(totalProduction)}</Typography>
                </StyledTableCell>
                <StyledTableCell align="left">
                  <Typography variant="body1">
                    {currencyFormatter(totalAccumulatedRevenue, currencySymbol)}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell align="left" colSpan={2}>
                  <Typography variant="body1">
                    {statusCountObjectToString(statusCountObject)}
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
              {data.results.map((item) => (
                <StyledTableRow
                  key={item.id}
                  style={{ backgroundColor: COLORS.white, cursor: "pointer" }}
                  onClick={() => handleClick(item.id)}
                >
                  <StyledTableCell>
                    <Typography variant="body1">{item.location}</Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body1" className={classes.capitalize}>
                      {getLabel(item.type.toLowerCase())}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body1">{nKilowattFormatter(item.production)}</Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body1">
                      {currencyFormatter(item.acc_revenue, currencySymbol)}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <StatusBox status={item.status} />
                  </StyledTableCell>
                  <StyledTableCell>{getActivity(item.power_assets_activity)}</StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </Collapse>
      </TableContainer>
    </div>
  );
};

ByTypeTable.defaultProps = {
  showActionButtons: false,
};
